<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { Trash2 } from 'lucide-vue-next'
import { formatApiError } from '@yuksalish/api-client'
import { useShopfloorApi } from '../composables/useShopfloorApi'
import ColorPickerModal from '../components/ColorPickerModal.vue'
import ProductPickerModal from '../components/ProductPickerModal.vue'
import { shopfloorRowTimestamp } from '../types/shopfloorDate'
import type {
  ColorReference,
  DailyKnittingOutput,
  DailyKnittingOutputListParams,
  DailyKnittingOutputSummaryRow,
  EmployeeOption,
  EquipmentItem,
  KnittingOutputDraftRow,
  KnittingShift,
  ProductOption,
  ShopfloorOrder,
  WarehouseAgentOption,
} from '../types/shopfloor'

const { t } = useI18n()
const route = useRoute()
const {
  listProducts,
  listSemiFinishedWarehouses,
  listEmployees,
  listEquipment,
  listCurrentProductionOrders,
  getOrder,
  createKnittingOutputBatch,
  listKnittingOutput,
  deleteKnittingOutput,
  summarizeKnittingOutput,
  listProductionBaskets,
} = useShopfloorApi()

const today = new Date().toLocaleDateString('en-CA')
const header = reactive({
  output_date: today,
  shift: 'day' as KnittingShift,
  product_id: 0,
  warehouse_agent_id: 0,
  employee_id: 0,
  machine_id: 0,
  production_order_id: 0,
  notes: '',
})
const historyFilters = reactive({
  date_from: today,
  date_to: today,
  product_id: 0,
  warehouse_agent_id: 0,
  shift: '',
  search: '',
  include_deleted: true,
  page: 1,
  per_page: 20,
})

/** Page size and safety cap used when search pulls every filtered row for local matching. */
const HISTORY_SEARCH_PAGE_SIZE = 100
const HISTORY_SEARCH_MAX_PAGES = 50

/**
 * The knitting-output list has no basket column, but each row carries
 * `production_lot_id` and every basket line carries the same lot id, so the
 * basket number is joined in from the basket list for the loaded date span.
 */
const BASKET_LOOKUP_PAGE_SIZE = 100
const BASKET_LOOKUP_MAX_PAGES = 5

const rows = ref<KnittingOutputDraftRow[]>([])
const products = ref<ProductOption[]>([])
const warehouses = ref<WarehouseAgentOption[]>([])
const employees = ref<EmployeeOption[]>([])
const machines = ref<EquipmentItem[]>([])
const orders = ref<ShopfloorOrder[]>([])
const history = ref<DailyKnittingOutput[]>([])
const summary = ref<DailyKnittingOutputSummaryRow[]>([])
/** `production_lot_id` → basket number, filled in the background by `loadBasketCodes`. */
const basketCodes = ref<Record<number, string>>({})
const historyMeta = reactive({ page: 1, per_page: 20, total: 0, total_pages: 1 })
const loadingLookups = ref(true)
const loadingHistory = ref(true)
const deletingBatch = ref<number | null>(null)
const submitting = ref(false)
const error = ref('')
const actionError = ref('')
const actionMessage = ref('')
const lastSaved = ref<DailyKnittingOutput[]>([])
const batchKey = ref(newBatchKey())
const showOutputForm = ref(false)
const colorPickerOpen = ref(false)
const productPickerOpen = ref(false)
const advancedOpen = ref(false)
const loadingOrderPlan = ref(false)
let orderPlanRequest = 0
/** Row wizard: one question per step — colour, size, quantity, lot number, comment. */
const ROW_WIZARD_STEPS = 5
const rowWizardOpen = ref(false)
const rowWizardStep = ref(1)
const editingRowId = ref('')
const rowDraft = ref<KnittingOutputDraftRow>(newDraftRow())
const sizeRangeOptions = ['0-6M', '6-12M', '12-24M', '23-25', '26-28', '29-31', '32-34', '35-37', '38-40', '39-41', '41-43', '42-44', '44-46']

const totalQuantity = computed(() =>
  rows.value.reduce((total, row) => total + normalizeQuantity(row.quantity), 0),
)
const validRows = computed(() =>
  rows.value.filter((row) => row.color_name.trim() && row.size_label.trim() && normalizeQuantity(row.quantity) > 0),
)
const selectedProduct = computed(() => products.value.find((product) => product.id === header.product_id))
const selectedOrder = computed(() => orders.value.find((order) => order.id === header.production_order_id) ?? null)
const hasPredefinedMatrix = computed(() => Boolean(selectedOrder.value?.variants?.length))
const canSubmit = computed(
  () =>
    !submitting.value &&
    header.product_id > 0 &&
    header.warehouse_agent_id > 0 &&
    header.shift &&
    validRows.value.length > 0 &&
    (hasPredefinedMatrix.value || validRows.value.length === rows.value.length),
)
const historyTotalPages = computed(() => Math.max(1, historyMeta.total_pages || 1))
const canGoPreviousHistory = computed(() => historyFilters.page > 1)
const canGoNextHistory = computed(() => historyFilters.page < historyTotalPages.value)

/** Quantity total of the rows currently rendered (the table footer). */
const historyPageTotal = computed(() =>
  history.value.reduce((total, item) => total + Number(item.quantity || 0), 0),
)

/**
 * Quantity total across EVERY row matching the active filters. The server
 * summary endpoint aggregates without a limit, so summing its groups (or the
 * local groups when a text search is active) is the exact filtered total.
 */
const historyFilteredTotal = computed(() =>
  summary.value.reduce((total, row) => total + Number(row.quantity || 0), 0),
)

/** True when the table shows only part of the filtered rows. */
const historyHasMoreRows = computed(() => historyMeta.total > history.value.length)
const historyProduct = computed(
  () => products.value.find((product) => product.id === historyFilters.product_id) ?? null,
)
const historyProductLabel = computed(() =>
  historyProduct.value ? productLabel(historyProduct.value) : t('shopfloor.knittingOutput.allProducts'),
)

const rowWizardMode = computed(() =>
  editingRowId.value
    ? t('shopfloor.knittingOutput.editRowTitle')
    : t('shopfloor.knittingOutput.addRowTitle'),
)
const rowStepTitle = computed(() => {
  const titles = [
    t('shopfloor.knittingOutput.askColor'),
    t('shopfloor.knittingOutput.askSize'),
    t('shopfloor.knittingOutput.askQuantity'),
    t('shopfloor.knittingOutput.askLot'),
    t('shopfloor.knittingOutput.askNotes'),
  ]
  return titles[rowWizardStep.value - 1] ?? ''
})

function newDraftRow(): KnittingOutputDraftRow {
  const random =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`
  return { clientRowId: random, color_name: '', size_label: '', quantity: '', lot_number: '', notes: '' }
}

function variantKey(color: string, size: string): string {
  return `${color.trim().toLocaleLowerCase()}\u0000${size.trim().toLocaleLowerCase()}`
}

async function loadFinishedByVariant(orderId: number): Promise<Map<string, number>> {
  const finished = new Map<string, number>()
  let page = 1
  let totalPages = 1
  do {
    const result = await listKnittingOutput({ production_order_id: orderId, page, per_page: 100 })
    for (const item of result.items) {
      if (isDeleted(item)) continue
      const key = variantKey(item.color_name, item.size_label)
      finished.set(key, (finished.get(key) ?? 0) + Number(item.quantity || 0))
    }
    totalPages = result.meta.total_pages
    page += 1
  } while (page <= totalPages)
  return finished
}

function orderVariantRows(order: ShopfloorOrder, finished: Map<string, number>): KnittingOutputDraftRow[] {
  return (order.variants ?? []).map((variant) => ({
    ...newDraftRow(),
    color_name: variant.color_name,
    size_label: variant.size_label,
    quantity: '',
    planned_quantity: Number(variant.planned_quantity) || 0,
    finished_quantity: finished.get(variantKey(variant.color_name, variant.size_label)) ?? 0,
  }))
}

async function applySelectedOrder(orderId: number): Promise<void> {
  let order = orders.value.find((item) => item.id === orderId)
  if (!order) return
  const request = ++orderPlanRequest
  loadingOrderPlan.value = true
  actionError.value = ''
  try {
    const [loadedOrder, finished] = await Promise.all([
      getOrder(orderId),
      loadFinishedByVariant(orderId),
    ])
    order = loadedOrder
    if (request !== orderPlanRequest || header.production_order_id !== orderId) return
    orders.value = orders.value.map((item) => item.id === orderId ? { ...item, ...order } : item)
    header.product_id = order.product_id
    if (order.destination_warehouse_agent_id && warehouses.value.some((item) => item.id === order.destination_warehouse_agent_id)) {
      header.warehouse_agent_id = order.destination_warehouse_agent_id
    }
    rows.value = orderVariantRows(order, finished)
    closeRowWizard()
  } catch (cause) {
    if (request === orderPlanRequest) actionError.value = formatApiError(cause)
    return
  } finally {
    if (request === orderPlanRequest) loadingOrderPlan.value = false
  }
}

function newBatchKey(): string {
  const random =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`
  return `shopfloor-knitting:${random}`
}

function normalizeQuantity(value: number | string): number {
  const numeric = Number(value)
  return Number.isFinite(numeric) && numeric > 0 ? numeric : 0
}

async function loadLookups() {
  loadingLookups.value = true
  try {
    const [productRows, warehouseRows, employeeRows, machineRows, currentOrders] = await Promise.all([
      listProducts(),
      listSemiFinishedWarehouses(),
      listEmployees(),
      listEquipment(),
      listCurrentProductionOrders(),
    ])
    products.value = productRows
    warehouses.value = warehouseRows
    employees.value = employeeRows
    machines.value = machineRows
    orders.value = currentOrders
    header.product_id = header.product_id || productRows[0]?.id || 0
    header.warehouse_agent_id = header.warehouse_agent_id || warehouseRows[0]?.id || 0
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loadingLookups.value = false
  }
}

/** Filters shared by the paged list and the search-all-rows path (no text search). */
function historyBaseParams(): DailyKnittingOutputListParams {
  return {
    date_from: historyFilters.date_from || undefined,
    date_to: historyFilters.date_to || undefined,
    product_id: historyFilters.product_id || undefined,
    warehouse_agent_id: historyFilters.warehouse_agent_id || undefined,
    shift: (historyFilters.shift || undefined) as KnittingShift | undefined,
    include_deleted: historyFilters.include_deleted ? 1 : undefined,
  }
}

/** The search box matches model, colour, size, and warehouse name. */
function matchesHistorySearch(item: DailyKnittingOutput, term: string): boolean {
  return [item.product_name, item.color_name, item.size_label, item.warehouse_name].some((value) =>
    String(value ?? '').toLowerCase().includes(term),
  )
}

/** Walk every page for the active filters so search can span the whole result set. */
async function fetchAllHistoryRows(params: DailyKnittingOutputListParams): Promise<DailyKnittingOutput[]> {
  const collected: DailyKnittingOutput[] = []
  let page = 1
  let totalPages = 1
  do {
    const result = await listKnittingOutput({ ...params, page, per_page: HISTORY_SEARCH_PAGE_SIZE })
    collected.push(...result.items)
    totalPages = Math.max(1, result.meta.total_pages)
    page += 1
  } while (page <= totalPages && page <= HISTORY_SEARCH_MAX_PAGES)
  return collected
}

/** Group locally matched rows exactly like the server's product,color,size summary. */
function summarizeLocally(rows: DailyKnittingOutput[]): DailyKnittingOutputSummaryRow[] {
  const groups = new Map<string, DailyKnittingOutputSummaryRow>()
  for (const row of rows) {
    const key = `${row.product_id}|${row.color_name}|${row.size_label}`
    const existing = groups.get(key)
    if (existing) {
      existing.quantity += Number(row.quantity || 0)
      existing.row_count += 1
    } else {
      groups.set(key, {
        product_id: row.product_id,
        product_name: row.product_name,
        product_sku: row.product_sku,
        color_name: row.color_name,
        size_label: row.size_label,
        quantity: Number(row.quantity || 0),
        row_count: 1,
      })
    }
  }
  return [...groups.values()].sort((a, b) => Number(b.quantity) - Number(a.quantity))
}

async function loadHistory() {
  loadingHistory.value = true
  error.value = ''
  const term = historyFilters.search.trim().toLowerCase()
  try {
    if (term) {
      // Search spans all filtered rows (including warehouse name, which the API does not index),
      // so the full set is fetched once and paginated locally.
      const matched = (await fetchAllHistoryRows(historyBaseParams())).filter((item) =>
        matchesHistorySearch(item, term),
      )
      const perPage = historyFilters.per_page
      const totalPages = Math.max(1, Math.ceil(matched.length / perPage))
      historyFilters.page = Math.min(Math.max(1, historyFilters.page), totalPages)
      const offset = (historyFilters.page - 1) * perPage
      history.value = matched.slice(offset, offset + perPage)
      historyMeta.page = historyFilters.page
      historyMeta.per_page = perPage
      historyMeta.total = matched.length
      historyMeta.total_pages = totalPages
      summary.value = summarizeLocally(matched)
    } else {
      const params: DailyKnittingOutputListParams = {
        ...historyBaseParams(),
        page: historyFilters.page,
        per_page: historyFilters.per_page,
      }
      const [listResult, summaryRows] = await Promise.all([
        listKnittingOutput(params),
        summarizeKnittingOutput({ ...params, group_by: 'product,color,size' }),
      ])
      history.value = listResult.items
      historyMeta.page = listResult.meta.page
      historyMeta.per_page = listResult.meta.per_page
      historyMeta.total = listResult.meta.total
      historyMeta.total_pages = listResult.meta.total_pages
      summary.value = summaryRows
    }
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loadingHistory.value = false
  }
  // Basket numbers are joined in the background so the table paints immediately.
  void loadBasketCodes(history.value)
}

function applyHistoryFilters() {
  historyFilters.page = 1
  void loadHistory()
}

function changeHistoryPage(nextPage: number) {
  historyFilters.page = Math.min(Math.max(1, nextPage), historyTotalPages.value)
  void loadHistory()
}

/** Only colour, size and quantity are required; lot number and comment may be skipped. */
function rowStepComplete(step: number): boolean {
  if (step === 1) return rowDraft.value.color_name.trim().length > 0
  if (step === 2) return rowDraft.value.size_label.trim().length > 0
  if (step === 3) return normalizeQuantity(rowDraft.value.quantity) > 0
  return true
}

function openRowWizard(row?: KnittingOutputDraftRow): void {
  editingRowId.value = row?.clientRowId ?? ''
  rowDraft.value = row
    ? {
        clientRowId: row.clientRowId,
        color_name: row.color_name,
        size_label: row.size_label,
        quantity: row.quantity,
        planned_quantity: row.planned_quantity,
        lot_number: row.lot_number ?? '',
        notes: row.notes ?? '',
      }
    : newDraftRow()
  rowWizardStep.value = 1
  rowWizardOpen.value = true
  actionError.value = ''
}

function closeRowWizard(): void {
  rowWizardOpen.value = false
  rowWizardStep.value = 1
  editingRowId.value = ''
}

function nextRowStep(): void {
  if (rowWizardStep.value < ROW_WIZARD_STEPS && rowStepComplete(rowWizardStep.value)) {
    rowWizardStep.value += 1
  }
}

function previousRowStep(): void {
  rowWizardStep.value = Math.max(1, rowWizardStep.value - 1)
}

/** Enter advances the wizard; on the last question it saves the row. */
function advanceRowStep(): void {
  if (rowWizardStep.value < ROW_WIZARD_STEPS) {
    nextRowStep()
    return
  }
  commitRow()
}

/** Save the wizard draft into the row list: replace when editing, append otherwise. */
function commitRow(): void {
  if (!rowStepComplete(1) || !rowStepComplete(2) || !rowStepComplete(3)) return
  const draft: KnittingOutputDraftRow = { ...rowDraft.value }
  const index = rows.value.findIndex((row) => row.clientRowId === draft.clientRowId)
  rows.value =
    index >= 0
      ? rows.value.map((row, position) => (position === index ? draft : row))
      : [...rows.value, draft]
  closeRowWizard()
}

function removeRow(clientRowId: string) {
  rows.value = rows.value.filter((row) => row.clientRowId !== clientRowId)
  if (editingRowId.value === clientRowId) closeRowWizard()
}

function resetDraft(clearFeedback = true) {
  rows.value = []
  closeRowWizard()
  header.notes = ''
  batchKey.value = newBatchKey()
  if (clearFeedback) {
    actionError.value = ''
    actionMessage.value = ''
    lastSaved.value = []
  }
}

function openNewOutput() {
  resetDraft()
  advancedOpen.value = false
  showOutputForm.value = true
}

async function submitBatch() {
  actionError.value = ''
  actionMessage.value = ''
  if (!canSubmit.value) {
    actionError.value = t('shopfloor.knittingOutput.validation')
    return
  }
  submitting.value = true
  try {
    const saved = await createKnittingOutputBatch({
      product_id: header.product_id,
      warehouse_agent_id: header.warehouse_agent_id,
      shift: header.shift,
      output_date: header.output_date,
      production_order_id: header.production_order_id || undefined,
      employee_id: header.employee_id || undefined,
      machine_id: header.machine_id || undefined,
      notes: header.notes.trim() || undefined,
      idempotency_key: batchKey.value,
      rows: validRows.value.map((row) => ({
        color_name: row.color_name.trim(),
        size_label: row.size_label.trim(),
        quantity: normalizeQuantity(row.quantity),
        lot_number: row.lot_number?.trim() || undefined,
        notes: row.notes?.trim() || undefined,
      })),
    })
    lastSaved.value = saved
    resetDraft(false)
    advancedOpen.value = false
    await loadHistory()
    actionMessage.value = t('shopfloor.knittingOutput.saved', { count: saved.length })
    showOutputForm.value = false
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}

function productLabel(product: ProductOption): string {
  const sku = product.sku ?? product.product_sku
  return sku ? `${product.name} (${sku})` : product.name
}

function isDeleted(item: DailyKnittingOutput): boolean {
  return Boolean(item.deleted_at || item.status === 'deleted' || item.status === 'cancelled')
}

/** History "Date" cell — `dd.mm.yy hh:mm`, falling back to the date column. */
function historyTimestamp(item: DailyKnittingOutput): string {
  return shopfloorRowTimestamp(item.created_at, item.output_date)
}

/** Basket number printed on the sticker (`—` when the row predates baskets). */
function basketNumber(item: DailyKnittingOutput): string {
  const lotId = Number(item.production_lot_id)
  if (!Number.isFinite(lotId) || lotId <= 0) return '—'
  return basketCodes.value[lotId] || '—'
}

/** Worker who knitted the row (falls back to whoever posted it). */
function workerName(item: DailyKnittingOutput): string {
  return String(item.employee_name ?? '').trim() || String(item.created_by_user_name ?? '').trim() || '—'
}

/**
 * Resolve the basket numbers of the given history rows. The window is derived
 * from the rows themselves (their `output_date` span), so the lookup stays
 * small; unresolved rows simply keep the `—` placeholder.
 */
async function loadBasketCodes(items: DailyKnittingOutput[]): Promise<void> {
  const wanted = new Set<number>()
  const dates: string[] = []
  for (const item of items) {
    const lotId = Number(item.production_lot_id)
    if (Number.isFinite(lotId) && lotId > 0) wanted.add(lotId)
    const date = String(item.output_date ?? '').slice(0, 10)
    if (date) dates.push(date)
  }
  if (wanted.size === 0 || dates.length === 0) {
    basketCodes.value = {}
    return
  }

  dates.sort()
  const resolved: Record<number, string> = {}
  try {
    let page = 1
    let totalPages = 1
    do {
      const result = await listProductionBaskets({
        date_from: dates[0],
        date_to: dates[dates.length - 1],
        per_page: BASKET_LOOKUP_PAGE_SIZE,
        page,
      })
      for (const basket of result.items) {
        for (const line of basket.lines ?? []) {
          const lotId = Number(line.production_lot_id)
          if (wanted.has(lotId)) resolved[lotId] = basket.basket_code
        }
      }
      totalPages = Math.max(1, result.meta.total_pages)
      page += 1
    } while (page <= totalPages && page <= BASKET_LOOKUP_MAX_PAGES && Object.keys(resolved).length < wanted.size)
  } catch {
    // Basket numbers are supplementary — never break the history table for them.
  }
  basketCodes.value = resolved
}

async function deleteBatch(item: DailyKnittingOutput) {
  if (!window.confirm(t('shopfloor.knittingOutput.deleteConfirm'))) return
  deletingBatch.value = item.id
  actionError.value = ''
  actionMessage.value = ''
  try {
    // scope=batch removes every row submitted together under the same idempotency key.
    await deleteKnittingOutput(item.id, 'batch')
    actionMessage.value = t('shopfloor.knittingOutput.deleteSuccess')
    await loadHistory()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    deletingBatch.value = null
  }
}

function xmlEscape(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

function crc32Bytes(input: Uint8Array): number {
  let crc = -1
  for (let i = 0; i < input.length; i += 1) {
    crc ^= input[i]
    for (let j = 0; j < 8; j += 1) {
      crc = (crc >>> 1) ^ (0xedb88320 & -(crc & 1))
    }
  }
  return (crc ^ -1) >>> 0
}

function uint16(value: number): number[] {
  return [value & 255, (value >>> 8) & 255]
}

function uint32(value: number): number[] {
  return [value & 255, (value >>> 8) & 255, (value >>> 16) & 255, (value >>> 24) & 255]
}

function makeZip(files: Array<{ name: string; content: string }>): Blob {
  const encoder = new TextEncoder()
  const chunks: Uint8Array[] = []
  const central: Uint8Array[] = []
  let offset = 0
  for (const file of files) {
    const name = encoder.encode(file.name)
    const content = encoder.encode(file.content)
    const checksum = crc32Bytes(content)
    const local = new Uint8Array([
      ...uint32(0x04034b50), ...uint16(20), ...uint16(0), ...uint16(0), ...uint16(0), ...uint16(0),
      ...uint32(checksum), ...uint32(content.length), ...uint32(content.length), ...uint16(name.length), ...uint16(0),
      ...name, ...content,
    ])
    chunks.push(local)
    central.push(new Uint8Array([
      ...uint32(0x02014b50), ...uint16(20), ...uint16(20), ...uint16(0), ...uint16(0), ...uint16(0), ...uint16(0),
      ...uint32(checksum), ...uint32(content.length), ...uint32(content.length), ...uint16(name.length), ...uint16(0),
      ...uint16(0), ...uint16(0), ...uint16(0), ...uint32(0), ...uint32(offset), ...name,
    ]))
    offset += local.length
  }
  const centralSize = central.reduce((sum, item) => sum + item.length, 0)
  const end = new Uint8Array([
    ...uint32(0x06054b50), ...uint16(0), ...uint16(0), ...uint16(files.length), ...uint16(files.length),
    ...uint32(centralSize), ...uint32(offset), ...uint16(0),
  ])
  return new Blob([...chunks, ...central, end], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
}

function exportXlsx() {
  const headings = [
    t('shopfloor.knittingOutput.date'),
    t('shopfloor.knittingOutput.basket'),
    t('shopfloor.knittingOutput.lotNumber'),
    t('shopfloor.knittingOutput.product'),
    t('shopfloor.knittingOutput.color'),
    t('shopfloor.knittingOutput.size'),
    t('shopfloor.knittingOutput.quantity'),
    t('shopfloor.knittingOutput.warehouse'),
    t('shopfloor.knittingOutput.shift'),
    t('shopfloor.knittingOutput.worker'),
    t('shopfloor.knittingOutput.status'),
  ]
  const sheetRows = [headings, ...history.value.map((item) => [
    historyTimestamp(item),
    basketNumber(item),
    item.lot_number,
    item.product_name,
    item.color_name,
    item.size_label,
    item.quantity,
    item.warehouse_name,
    t(`shopfloor.knittingOutput.${item.shift}`),
    workerName(item),
    isDeleted(item) ? t('shopfloor.knittingOutput.deleted') : t('shopfloor.knittingOutput.active'),
  ])]
  const rowsXml = sheetRows.map((row, rowIndex) =>
    `<row r="${rowIndex + 1}">${row.map((cell, cellIndex) => {
      const col = String.fromCharCode(65 + cellIndex)
      const value = typeof cell === 'number' ? cell : xmlEscape(cell)
      return typeof cell === 'number'
        ? `<c r="${col}${rowIndex + 1}"><v>${value}</v></c>`
        : `<c r="${col}${rowIndex + 1}" t="inlineStr"><is><t>${value}</t></is></c>`
    }).join('')}</row>`,
  ).join('')
  const files = [
    { name: '[Content_Types].xml', content: '<?xml version="1.0" encoding="UTF-8"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/></Types>' },
    { name: '_rels/.rels', content: '<?xml version="1.0" encoding="UTF-8"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>' },
    { name: 'xl/workbook.xml', content: '<?xml version="1.0" encoding="UTF-8"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="Knitting output" sheetId="1" r:id="rId1"/></sheets></workbook>' },
    { name: 'xl/_rels/workbook.xml.rels', content: '<?xml version="1.0" encoding="UTF-8"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/></Relationships>' },
    { name: 'xl/worksheets/sheet1.xml', content: `<?xml version="1.0" encoding="UTF-8"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>${rowsXml}</sheetData></worksheet>` },
  ]
  const url = URL.createObjectURL(makeZip(files))
  const link = document.createElement('a')
  link.href = url
  link.download = `knitting-output-${historyFilters.date_from || today}-${historyFilters.date_to || today}.xlsx`
  link.click()
  URL.revokeObjectURL(url)
}

function employeeLabel(employee: EmployeeOption): string {
  return `${employee.first_name} ${employee.last_name}`.trim() || employee.employee_code
}

function selectHistoryProduct(product: ProductOption): void {
  historyFilters.product_id = product.id
  productPickerOpen.value = false
}

function clearHistoryProduct(): void {
  historyFilters.product_id = 0
}

function openRowColorPicker(): void {
  colorPickerOpen.value = true
}

function selectColor(color: ColorReference): void {
  rowDraft.value.color_name = color.name
  colorPickerOpen.value = false
}

function selectSize(size: string): void {
  rowDraft.value.size_label = size
}

function makeLotToken(value: string, fallback: string): string {
  const token = value
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 14)
  return token || fallback
}

function generateLotNumber(): void {
  const date = header.output_date.replace(/-/g, '') || today.replace(/-/g, '')
  const color = makeLotToken(rowDraft.value.color_name, 'COLOR')
  const size = makeLotToken(rowDraft.value.size_label, 'SIZE')
  const random = Math.random().toString(36).slice(2, 6).toUpperCase()
  rowDraft.value.lot_number = `KNT-${date}-${color}-${size}-${random}`
}

function summaryDimension(row: DailyKnittingOutputSummaryRow): string {
  return [row.product_name, row.color_name, row.size_label].filter(Boolean).join(' / ')
}

watch(() => [
  historyFilters.date_from,
  historyFilters.date_to,
  historyFilters.product_id,
  historyFilters.warehouse_agent_id,
  historyFilters.shift,
  historyFilters.search,
  historyFilters.include_deleted,
  historyFilters.per_page,
], () => {
  applyHistoryFilters()
})

watch(() => header.production_order_id, (orderId) => {
  if (orderId > 0) void applySelectedOrder(orderId)
  else {
    orderPlanRequest += 1
    loadingOrderPlan.value = false
    rows.value = []
    closeRowWizard()
  }
})

onMounted(async () => {
  await loadLookups()
  const requestedOrderId = Number(route.query.order)
  if (Number.isInteger(requestedOrderId) && requestedOrderId > 0 && orders.value.some((order) => order.id === requestedOrderId)) {
    header.production_order_id = requestedOrderId
    advancedOpen.value = true
    showOutputForm.value = true
  }
  void loadHistory()
})
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('shopfloor.knittingOutput.eyebrow') }}</p>
      <h2>{{ t('shopfloor.knittingOutput.title') }}</h2>
      <p>{{ t('shopfloor.knittingOutput.description') }}</p>
    </div>
    <div class="page-toolbar">
      <button type="button" class="btn-primary" @click="openNewOutput">{{ t('shopfloor.knittingOutput.newOutput') }}</button>
    </div>
  </div>

  <div v-if="error" class="state-message error compact">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="loadHistory">{{ t('shopfloor.state.retry') }}</button>
  </div>
  <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>
  <small v-if="actionError && !showOutputForm" class="error">{{ actionError }}</small>

  <div class="summary-row" v-if="summary.length">
    <div v-for="(item, index) in summary.slice(0, 4)" :key="index" class="summary-card">
      <span class="summary-value good">{{ Number(item.quantity || 0).toLocaleString() }}</span>
      <span class="summary-label">{{ summaryDimension(item) }}</span>
    </div>
  </div>

  <section class="panel">
    <div class="panel-header">
      <h3>{{ t('shopfloor.knittingOutput.history') }}</h3>
      <div class="history-actions">
        <button class="btn-secondary" type="button" :disabled="history.length === 0" @click="exportXlsx">{{ t('shopfloor.knittingOutput.exportXlsx') }}</button>
        <button class="btn-secondary" type="button" @click="loadHistory">{{ t('shopfloor.state.retry') }}</button>
      </div>
    </div>
    <div class="history-filters">
      <div class="filter-field date-range-field">
        <span class="filter-caption">{{ t('shopfloor.knittingOutput.dateRange') }}</span>
        <div class="date-range">
          <input
            v-model="historyFilters.date_from"
            type="date"
            :aria-label="t('shopfloor.knittingOutput.dateFrom')"
          />
          <span class="date-range-sep" aria-hidden="true">–</span>
          <input
            v-model="historyFilters.date_to"
            type="date"
            :aria-label="t('shopfloor.knittingOutput.dateTo')"
          />
        </div>
      </div>
      <div class="filter-field">
        <span class="filter-caption">{{ t('shopfloor.knittingOutput.product') }}</span>
        <div class="filter-picker">
          <button type="button" class="field-button" @click="productPickerOpen = true">
            <span>{{ historyProductLabel }}</span>
            <span aria-hidden="true">+</span>
          </button>
          <button
            v-if="historyFilters.product_id"
            type="button"
            class="filter-clear"
            :aria-label="t('shopfloor.knittingOutput.clearModel')"
            @click="clearHistoryProduct"
          >
            ×
          </button>
        </div>
      </div>
      <label>{{ t('shopfloor.knittingOutput.warehouse') }}
        <select v-model.number="historyFilters.warehouse_agent_id">
          <option :value="0">{{ t('shopfloor.knittingOutput.allWarehouses') }}</option>
          <option v-for="warehouse in warehouses" :key="warehouse.id" :value="warehouse.id">{{ warehouse.name }}</option>
        </select>
      </label>
      <label>{{ t('shopfloor.knittingOutput.shift') }}
        <select v-model="historyFilters.shift">
          <option value="">{{ t('shopfloor.knittingOutput.allShifts') }}</option>
          <option value="day">{{ t('shopfloor.knittingOutput.day') }}</option>
          <option value="night">{{ t('shopfloor.knittingOutput.night') }}</option>
        </select>
      </label>
      <label>{{ t('shopfloor.knittingOutput.search') }}
        <input v-model="historyFilters.search" type="search" :placeholder="t('shopfloor.knittingOutput.searchPlaceholder')" />
      </label>
      <label>{{ t('shopfloor.knittingOutput.perPage') }}
        <select v-model.number="historyFilters.per_page">
          <option :value="10">10</option>
          <option :value="20">20</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
      </label>
      <label class="check-field">
        <input v-model="historyFilters.include_deleted" type="checkbox" />
        <span>{{ t('shopfloor.knittingOutput.showDeleted') }}</span>
      </label>
      <button class="btn-primary" type="button" @click="applyHistoryFilters">{{ t('shopfloor.knittingOutput.applyFilters') }}</button>
    </div>
    <div v-if="loadingHistory" class="state-message compact">{{ t('shopfloor.knittingOutput.loading') }}</div>
    <div v-else-if="history.length === 0" class="state-message compact">{{ t('shopfloor.knittingOutput.empty') }}</div>
    <div v-else class="table-scroll">
      <table class="data-table history-table">
        <thead>
          <tr>
            <th>{{ t('shopfloor.knittingOutput.date') }}</th>
            <th>{{ t('shopfloor.knittingOutput.basket') }}</th>
            <th>{{ t('shopfloor.knittingOutput.lotWarehouse') }}</th>
            <th>{{ t('shopfloor.knittingOutput.product') }}</th>
            <th>{{ t('shopfloor.knittingOutput.color') }}</th>
            <th>{{ t('shopfloor.knittingOutput.size') }}</th>
            <th>{{ t('shopfloor.knittingOutput.quantity') }}</th>
            <th>{{ t('shopfloor.knittingOutput.shift') }}</th>
            <th>{{ t('shopfloor.knittingOutput.worker') }}</th>
            <th>{{ t('shopfloor.knittingOutput.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in history" :key="item.id" :class="{ 'is-deleted': isDeleted(item) }">
            <td :data-label="t('shopfloor.knittingOutput.date')">{{ historyTimestamp(item) }}</td>
            <td class="cell-code" :data-label="t('shopfloor.knittingOutput.basket')">{{ basketNumber(item) }}</td>
            <td :data-label="t('shopfloor.knittingOutput.lotWarehouse')">
              <span class="cell-code">{{ item.lot_number }}</span>
              <span class="cell-stack-muted">{{ item.warehouse_name }}</span>
            </td>
            <td :data-label="t('shopfloor.knittingOutput.product')">{{ item.product_name }}</td>
            <td :data-label="t('shopfloor.knittingOutput.color')">{{ item.color_name }}</td>
            <td :data-label="t('shopfloor.knittingOutput.size')">{{ item.size_label }}</td>
            <td class="cell-qty" :data-label="t('shopfloor.knittingOutput.quantity')">{{ Number(item.quantity || 0).toLocaleString() }}</td>
            <td :data-label="t('shopfloor.knittingOutput.shift')">{{ t(`shopfloor.knittingOutput.${item.shift}`) }}</td>
            <td :data-label="t('shopfloor.knittingOutput.worker')">{{ workerName(item) }}</td>
            <td :data-label="t('shopfloor.knittingOutput.actions')">
              <button
                v-if="!isDeleted(item)"
                type="button"
                class="link-action danger icon-action"
                :disabled="deletingBatch === item.id"
                :title="t('shopfloor.knittingOutput.deleteBatch')"
                :aria-label="deletingBatch === item.id ? t('shopfloor.knittingOutput.deleting') : t('shopfloor.knittingOutput.deleteBatch')"
                @click="deleteBatch(item)"
              >
                <Trash2 :size="16" aria-hidden="true" />
              </button>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr class="history-total">
            <td colspan="6">
              {{ t('shopfloor.knittingOutput.pageTotal') }} · {{ historyMeta.total }}
              {{ t('shopfloor.knittingOutput.rowCount') }}
            </td>
            <td class="cell-qty">{{ historyPageTotal.toLocaleString() }}</td>
            <td colspan="3">
              <template v-if="historyHasMoreRows">
                {{ t('shopfloor.knittingOutput.filteredTotal') }}:
                {{ historyFilteredTotal.toLocaleString() }}
              </template>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
    <div class="pagination">
      <span>{{ t('shopfloor.knittingOutput.pageOf', { page: historyFilters.page, total: historyTotalPages, count: historyMeta.total }) }}</span>
      <button class="btn-secondary" type="button" :disabled="!canGoPreviousHistory" @click="changeHistoryPage(historyFilters.page - 1)">
        {{ t('shopfloor.pagination.previous') }}
      </button>
      <button class="btn-secondary" type="button" :disabled="!canGoNextHistory" @click="changeHistoryPage(historyFilters.page + 1)">
        {{ t('shopfloor.pagination.next') }}
      </button>
    </div>
  </section>

  <div v-if="showOutputForm" class="offcanvas-backdrop" @click.self="showOutputForm = false">
    <aside class="offcanvas-panel" role="dialog" aria-modal="true" :aria-label="t('shopfloor.knittingOutput.newOutput')">
      <form class="knitting-form" @submit.prevent="submitBatch">
        <header class="offcanvas-header">
          <div class="offcanvas-header-top">
            <div>
              <p class="eyebrow">{{ t('shopfloor.knittingOutput.eyebrow') }}</p>
              <h3>{{ t('shopfloor.knittingOutput.newOutput') }}</h3>
            </div>
            <button type="button" class="close-button" :aria-label="t('shopfloor.knittingOutput.close')" @click="showOutputForm = false">×</button>
          </div>
        </header>

        <section class="panel">
          <div class="panel-header">
            <h3>{{ t('shopfloor.knittingOutput.header') }}</h3>
            <span v-if="selectedProduct" class="product-chip">{{ productLabel(selectedProduct) }}</span>
          </div>
          <div v-if="selectedOrder" class="order-plan-banner">
            <strong>{{ selectedOrder.mo_code }}</strong>
            <span>{{ loadingOrderPlan ? t('shopfloor.knittingOutput.loadingOrderPlan') : t('shopfloor.knittingOutput.orderPlanLoaded', { count: selectedOrder.variants?.length ?? 0 }) }}</span>
          </div>
          <div class="form-fields">
            <label class="field-highlight">{{ t('shopfloor.knittingOutput.order') }}
              <select v-model.number="header.production_order_id" :disabled="loadingLookups">
                <option :value="0">{{ t('shopfloor.knittingOutput.noOrder') }}</option>
                <option v-for="order in orders" :key="order.id" :value="order.id">{{ order.mo_code }} · {{ order.product_name }}</option>
              </select>
            </label>
            <div class="field-row-2">
              <label>{{ t('shopfloor.knittingOutput.date') }}
                <input v-model="header.output_date" type="date" required />
              </label>
              <label>{{ t('shopfloor.knittingOutput.shift') }}
                <select v-model="header.shift" required>
                  <option value="day">{{ t('shopfloor.knittingOutput.day') }}</option>
                  <option value="night">{{ t('shopfloor.knittingOutput.night') }}</option>
                </select>
              </label>
            </div>
            <label class="field-highlight">{{ t('shopfloor.knittingOutput.product') }}
              <select v-model.number="header.product_id" required :disabled="loadingLookups">
                <option :value="0" disabled>{{ t('shopfloor.knittingOutput.selectProduct') }}</option>
                <option v-for="product in products" :key="product.id" :value="product.id">{{ productLabel(product) }}</option>
              </select>
            </label>
            <label>{{ t('shopfloor.knittingOutput.warehouse') }}
              <select v-model.number="header.warehouse_agent_id" required :disabled="loadingLookups">
                <option :value="0" disabled>{{ t('shopfloor.knittingOutput.selectWarehouse') }}</option>
                <option v-for="warehouse in warehouses" :key="warehouse.id" :value="warehouse.id">{{ warehouse.name }}</option>
              </select>
            </label>
          </div>

          <div class="collapse-card">
            <button type="button" class="collapse-toggle" :aria-expanded="advancedOpen" @click="advancedOpen = !advancedOpen">
              <span>{{ t('shopfloor.knittingOutput.moreOptions') }}</span>
              <span class="collapse-hint" aria-hidden="true">{{ advancedOpen ? '−' : '+' }}</span>
            </button>
            <div v-show="advancedOpen" class="collapse-body">
              <label>{{ t('shopfloor.knittingOutput.employee') }}
                <select v-model.number="header.employee_id" :disabled="loadingLookups">
                  <option :value="0">{{ t('shopfloor.knittingOutput.optional') }}</option>
                  <option v-for="employee in employees" :key="employee.id" :value="employee.id">{{ employeeLabel(employee) }}</option>
                </select>
              </label>
              <label>{{ t('shopfloor.knittingOutput.machine') }}
                <select v-model.number="header.machine_id" :disabled="loadingLookups">
                  <option :value="0">{{ t('shopfloor.knittingOutput.optional') }}</option>
                  <option v-for="machine in machines" :key="machine.id" :value="machine.id">{{ machine.name }}</option>
                </select>
              </label>
              <label>{{ t('shopfloor.knittingOutput.notes') }}
                <input v-model="header.notes" type="text" maxlength="500" />
              </label>
            </div>
          </div>
        </section>

        <section class="panel">
          <template v-if="rowWizardOpen && !hasPredefinedMatrix">
            <div class="row-wizard" @keydown.enter.prevent="advanceRowStep">
              <div class="row-wizard-head">
                <button type="button" class="link-action" @click="closeRowWizard">{{ t('shopfloor.knittingOutput.cancel') }}</button>
                <span class="row-wizard-progress">{{ t('shopfloor.knittingOutput.rowStepOf', { current: rowWizardStep, total: ROW_WIZARD_STEPS }) }}</span>
              </div>
              <div class="row-wizard-dots" aria-hidden="true">
                <span
                  v-for="dot in ROW_WIZARD_STEPS"
                  :key="dot"
                  class="row-wizard-dot"
                  :class="{ 'is-current': rowWizardStep === dot, 'is-done': rowWizardStep > dot }"
                />
              </div>
              <p class="row-wizard-mode">{{ rowWizardMode }}</p>
              <h3 class="row-wizard-title">{{ rowStepTitle }}</h3>

              <div v-show="rowWizardStep === 1" class="row-wizard-body">
                <button type="button" class="field-button large" @click="openRowColorPicker">
                  <span>{{ rowDraft.color_name || t('shopfloor.knittingOutput.selectColor') }}</span>
                  <span aria-hidden="true">+</span>
                </button>
              </div>

              <div v-show="rowWizardStep === 2" class="row-wizard-body">
                <input
                  v-model="rowDraft.size_label"
                  type="text"
                  maxlength="120"
                  list="knitting-size-ranges"
                  :placeholder="t('shopfloor.knittingOutput.sizePlaceholder')"
                />
                <div class="size-suggestions" :aria-label="t('shopfloor.knittingOutput.sizeSuggestions')">
                  <button
                    v-for="size in sizeRangeOptions"
                    :key="size"
                    type="button"
                    :class="{ active: rowDraft.size_label === size }"
                    @click="selectSize(size)"
                  >
                    {{ size }}
                  </button>
                </div>
                <datalist id="knitting-size-ranges">
                  <option v-for="size in sizeRangeOptions" :key="size" :value="size" />
                </datalist>
              </div>

              <div v-show="rowWizardStep === 3" class="row-wizard-body">
                <input v-model="rowDraft.quantity" class="qty-input" type="number" min="0.01" step="0.01" inputmode="decimal" />
              </div>

              <div v-show="rowWizardStep === 4" class="row-wizard-body">
                <div class="inline-action-input">
                  <input v-model="rowDraft.lot_number" type="text" />
                  <button
                    type="button"
                    class="inside-action"
                    :title="t('shopfloor.knittingOutput.generate')"
                    :aria-label="t('shopfloor.knittingOutput.generate')"
                    @click="generateLotNumber"
                  >
                    +
                  </button>
                </div>
              </div>

              <div v-show="rowWizardStep === 5" class="row-wizard-body">
                <input v-model="rowDraft.notes" type="text" maxlength="500" />
                <div class="row-preview">
                  <strong>{{ rowDraft.color_name }} · {{ rowDraft.size_label }}</strong>
                  <span>{{ t('shopfloor.knittingOutput.quantity') }}: {{ normalizeQuantity(rowDraft.quantity).toLocaleString() }}</span>
                  <span v-if="rowDraft.lot_number">{{ t('shopfloor.knittingOutput.lotNumber') }}: {{ rowDraft.lot_number }}</span>
                </div>
              </div>

              <div class="row-wizard-footer">
                <button type="button" class="btn-secondary" :disabled="rowWizardStep === 1" @click="previousRowStep">
                  {{ t('shopfloor.knittingOutput.back') }}
                </button>
                <button
                  v-if="rowWizardStep < ROW_WIZARD_STEPS"
                  type="button"
                  class="btn-primary"
                  :disabled="!rowStepComplete(rowWizardStep)"
                  @click="nextRowStep"
                >
                  {{ t('shopfloor.knittingOutput.next') }}
                </button>
                <button v-else type="button" class="btn-primary" @click="commitRow">
                  {{ editingRowId ? t('shopfloor.knittingOutput.saveRow') : t('shopfloor.knittingOutput.addRow') }}
                </button>
              </div>
            </div>
          </template>

          <template v-else>
            <div class="panel-header">
              <h3>{{ t('shopfloor.knittingOutput.rows') }}</h3>
              <button v-if="!hasPredefinedMatrix" type="button" class="btn-secondary" @click="openRowWizard()">{{ t('shopfloor.knittingOutput.addRow') }}</button>
            </div>
            <p class="wizard-hint">{{ t(hasPredefinedMatrix ? 'shopfloor.knittingOutput.matrixInputHint' : 'shopfloor.knittingOutput.rowsHint') }}</p>
            <div v-if="rows.length === 0" class="rows-empty">
              <p>{{ t('shopfloor.knittingOutput.rowsEmpty') }}</p>
              <button v-if="!hasPredefinedMatrix" type="button" class="btn-primary" @click="openRowWizard()">{{ t('shopfloor.knittingOutput.addFirstRow') }}</button>
            </div>
            <div v-else class="row-cards">
              <article v-for="row in rows" :key="row.clientRowId" class="row-card" :class="{ 'matrix-row': hasPredefinedMatrix }">
                <div class="row-card-main">
                  <strong>{{ row.color_name }}</strong>
                  <span>{{ row.size_label }}</span>
                  <small v-if="hasPredefinedMatrix" class="variant-progress">{{ t('shopfloor.knittingOutput.finishedPlanned', { finished: row.finished_quantity ?? 0, planned: row.planned_quantity ?? 0 }) }}</small>
                  <small v-else-if="row.planned_quantity">{{ t('shopfloor.knittingOutput.plannedForVariant', { count: row.planned_quantity }) }}</small>
                  <small v-if="row.lot_number">{{ t('shopfloor.knittingOutput.lotNumber') }}: {{ row.lot_number }}</small>
                  <small v-if="row.notes">{{ row.notes }}</small>
                </div>
                <input
                  v-if="hasPredefinedMatrix"
                  v-model="row.quantity"
                  class="matrix-quantity-input"
                  type="number"
                  min="0"
                  step="0.01"
                  inputmode="decimal"
                  :max="Math.max(0, (row.planned_quantity ?? 0) - (row.finished_quantity ?? 0))"
                  :disabled="(row.finished_quantity ?? 0) >= (row.planned_quantity ?? 0)"
                  :aria-label="t('shopfloor.knittingOutput.outputQuantity')"
                  :placeholder="t('shopfloor.knittingOutput.outputQuantity')"
                />
                <span v-else class="row-card-qty">{{ normalizeQuantity(row.quantity).toLocaleString() }}</span>
                <div v-if="!hasPredefinedMatrix" class="row-card-actions">
                  <button type="button" class="link-action" @click="openRowWizard(row)">{{ t('shopfloor.knittingOutput.editRow') }}</button>
                  <button type="button" class="link-action danger" @click="removeRow(row.clientRowId)">{{ t('shopfloor.knittingOutput.remove') }}</button>
                </div>
              </article>
            </div>
          </template>
        </section>

        <small v-if="actionError" class="error">{{ actionError }}</small>

        <div v-if="!rowWizardOpen" class="wizard-footer">
          <div v-if="rows.length" class="wizard-metrics">
            <div>
              <strong>{{ totalQuantity.toLocaleString() }}</strong>
              <span>{{ t('shopfloor.knittingOutput.totalQuantity') }}</span>
            </div>
            <div>
              <strong>{{ rows.length }}</strong>
              <span>{{ t('shopfloor.knittingOutput.rowCount') }}</span>
            </div>
          </div>
          <button v-if="rows.length" type="button" class="btn-secondary" @click="resetDraft">
            {{ t('shopfloor.knittingOutput.clear') }}
          </button>
          <button type="submit" class="btn-primary" :disabled="!canSubmit">
            {{ submitting ? t('shopfloor.knittingOutput.submitting') : t('shopfloor.knittingOutput.submit') }}
          </button>
        </div>
      </form>
    </aside>
  </div>

  <ColorPickerModal
    :open="colorPickerOpen"
    :title="t('shopfloor.knittingOutput.colorPickerTitle')"
    @close="colorPickerOpen = false"
    @select="selectColor"
  />

  <ProductPickerModal
    :open="productPickerOpen"
    :title="t('shopfloor.knittingOutput.modelPickerTitle')"
    :selected-id="historyFilters.product_id || null"
    @close="productPickerOpen = false"
    @select="selectHistoryProduct"
  />
</template>

<style scoped>
.page-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}
.eyebrow {
  margin: 0 0 4px;
  color: #65736e;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}
h2 { margin: 0 0 4px; font-size: 22px; font-weight: 700; }
.page-heading p { margin: 0; color: #5f6b6a; font-size: 14px; }
.page-toolbar {
  display: flex;
  flex: 0 0 auto;
  gap: 8px;
}

.knitting-form {
  display: grid;
  gap: 16px;
}
.row-wizard {
  display: grid;
  gap: 12px;
}
.row-wizard-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.row-wizard-progress {
  color: #65736e;
  font-size: 12px;
  font-weight: 700;
}
.row-wizard-dots {
  display: flex;
  gap: 6px;
}
.row-wizard-dot {
  flex: 1 1 auto;
  height: 6px;
  border-radius: 999px;
  background: #e2e6e3;
}
.row-wizard-dot.is-current {
  background: #27705d;
}
.row-wizard-dot.is-done {
  background: #a9cfc3;
}
.row-wizard-mode {
  margin: 0;
  color: #65736e;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}
.row-wizard-title {
  margin: -6px 0 0;
  color: #1a2b26;
  font-size: 20px;
  font-weight: 700;
}
.row-wizard-body {
  display: grid;
  gap: 10px;
}
.field-button.large {
  min-height: 52px;
  font-size: 15px;
  font-weight: 700;
}
.qty-input {
  padding: 14px 12px;
  font-size: 24px;
  font-weight: 800;
  text-align: center;
}
.row-wizard-footer {
  display: flex;
  gap: 10px;
}
.row-wizard-footer .btn-primary,
.row-wizard-footer .btn-secondary {
  flex: 1 1 0;
  min-height: 48px;
}
.row-preview {
  display: grid;
  gap: 4px;
  padding: 10px 12px;
  border: 1px solid #e2e6e3;
  border-radius: 8px;
  background: #f9fbfa;
}
.row-preview strong {
  font-size: 14px;
}
.row-preview span {
  color: #65736e;
  font-size: 13px;
}
.wizard-hint {
  margin: 0 0 10px;
  color: #65736e;
  font-size: 12px;
  font-weight: 600;
}
.collapse-card {
  margin-top: 14px;
  overflow: hidden;
  border: 1px solid #e2e6e3;
  border-radius: 8px;
  background: #f9fbfa;
}
.collapse-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 44px;
  padding: 10px 12px;
  border: 0;
  background: transparent;
  color: #27705d;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.collapse-hint {
  font-size: 18px;
  font-weight: 800;
  line-height: 1;
}
.collapse-body {
  display: grid;
  gap: 12px;
  padding: 0 12px 12px;
}
.rows-empty {
  display: grid;
  justify-items: center;
  gap: 10px;
  padding: 24px 12px;
  border: 1px dashed #d4d8d5;
  border-radius: 8px;
  color: #65736e;
  text-align: center;
}
.rows-empty p {
  margin: 0;
  font-size: 13px;
}
.row-cards {
  display: grid;
  gap: 8px;
}
.row-card {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 4px 12px;
  padding: 10px 12px;
  border: 1px solid #e2e6e3;
  border-radius: 8px;
  background: #fff;
}
.row-card-main {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.row-card-main strong {
  font-size: 14px;
}
.row-card-main span {
  color: #4a5a55;
  font-size: 13px;
}
.row-card-main small {
  margin-top: 2px;
  color: #65736e;
  font-size: 12px;
}
.row-card-qty {
  color: #1f5c4b;
  font-size: 20px;
  font-weight: 800;
}
.row-card-actions {
  display: flex;
  grid-column: 1 / -1;
  gap: 14px;
}
.row-card.matrix-row {
  grid-template-columns: minmax(0, 1fr) minmax(112px, 148px);
  gap: 12px;
}
.variant-progress {
  color: #27705d !important;
  font-weight: 700;
}
.matrix-quantity-input {
  width: 100%;
  min-width: 0;
  justify-self: end;
  text-align: right;
  font-size: 16px;
  font-weight: 700;
}
.form-fields {
  display: grid;
  gap: 12px;
}
.field-row-2 {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.field-highlight {
  color: #27705d;
}
.field-highlight select {
  border-color: #27705d;
  background: #eef5f2;
  box-shadow: 0 0 0 2px rgb(39 112 93 / 22%);
  font-weight: 700;
}
.panel {
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
  border: 1px solid #e2e6e3;
  border-radius: 8px;
}
.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}
.panel-header h3 { margin: 0; font-size: 16px; }
.table-scroll {
  overflow-x: auto;
}
.product-chip {
  padding: 4px 10px;
  border-radius: 999px;
  background: #f0f5f3;
  color: #1f5c4b;
  font-size: 12px;
  font-weight: 700;
}
.order-plan-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  padding: 10px 12px;
  border: 1px solid #efd09b;
  border-radius: 8px;
  color: #74420b;
  background: #fff8e8;
  font-size: 12px;
}
label {
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #65736e;
  font-size: 12px;
  font-weight: 700;
}
input,
select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d4d8d5;
  border-radius: 6px;
  background: #fff;
  color: #1a2b26;
  font-size: 13px;
}
.history-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
}
.history-filters {
  display: grid;
  grid-template-columns: repeat(5, minmax(130px, 1fr));
  gap: 10px;
  padding: 12px;
  margin-bottom: 14px;
  border: 1px solid #e2e6e3;
  border-radius: 8px;
  background: #f9fbfa;
}
.history-filters .filter-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #65736e;
  font-size: 12px;
  font-weight: 700;
}
.history-filters .date-range-field {
  grid-column: span 2;
}
.history-filters .date-range {
  display: flex;
  align-items: center;
  gap: 6px;
}
.history-filters .date-range input {
  min-width: 0;
}
.history-filters .date-range-sep {
  color: #8a9692;
}
.history-filters .filter-picker {
  display: flex;
  align-items: center;
  gap: 6px;
}
.history-filters .filter-picker .field-button {
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  font-weight: 400;
}
.history-filters .filter-clear {
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 1px solid #d4d8d5;
  border-radius: 6px;
  background: #fff;
  color: #65736e;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
}
.history-filters .filter-clear:hover {
  border-color: #b33a3a;
  color: #b33a3a;
}
.history-filters .check-field {
  justify-content: center;
}
.check-field {
  flex-direction: row;
  gap: 8px;
  align-items: center;
}
.check-field input {
  width: auto;
}
.data-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border: 1px solid #e2e6e3;
  border-radius: 8px;
  overflow: hidden;
}
.data-table th,
.data-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #f0f2f1;
  color: #1a2b26;
  font-size: 13px;
  text-align: left;
}
.data-table th {
  background: #f6f8f7;
  color: #65736e;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}
.history-table {
  min-width: 980px;
}
.history-table th,
.history-table td {
  white-space: nowrap;
}
/* Combined lot / warehouse cell — the warehouse sits under the lot number. */
.cell-stack-muted {
  display: block;
  color: #65736e;
  font-size: 11px;
  font-weight: 600;
}
/* Table footer: page quantity total (and the filtered total when paged). */
.history-total td {
  padding-top: 10px;
  border-top: 1px solid #dfe4e1;
  background: #f6f8f7;
  color: #1a2b26;
  font-size: 12px;
  font-weight: 800;
}
.history-total .cell-qty {
  color: var(--ui-primary, #55733c);
  font-size: 14px;
}
.data-table tr.is-deleted {
  background: #f4f6f5;
}
.data-table tr.is-deleted td {
  color: #8a9692;
  text-decoration: line-through;
}
.size-suggestions {
  display: flex;
  max-width: 260px;
  gap: 4px;
  overflow-x: auto;
  padding-bottom: 2px;
}
.size-suggestions button {
  flex: 0 0 auto;
  min-height: 28px;
  padding: 4px 8px;
  border: 1px solid #d4d8d5;
  border-radius: 999px;
  background: #fff;
  color: #27705d;
  font-size: 11px;
  font-weight: 800;
  cursor: pointer;
}
.size-suggestions button:hover,
.size-suggestions button.active {
  border-color: #27705d;
  background: #eef5f2;
}
.inline-action-input {
  position: relative;
  min-width: 220px;
}
.inline-action-input input {
  padding-right: 42px;
}
.inside-action {
  position: absolute;
  top: 4px;
  right: 4px;
  display: grid;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 6px;
  background: #f0f5f3;
  color: #27705d;
  font-size: 18px;
  font-weight: 800;
  line-height: 1;
  cursor: pointer;
  place-items: center;
}
.inside-action:hover {
  background: #eef5f2;
}
.field-button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-width: 220px;
  min-height: 35px;
  gap: 12px;
  padding: 8px 12px;
  border: 1px solid #d4d8d5;
  border-radius: 6px;
  background: #fff;
  color: #1a2b26;
  font: inherit;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
}
.field-button:hover {
  border-color: #27705d;
}
.field-button span:first-child {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.field-button span:last-child {
  color: #27705d;
  font-weight: 800;
}
.wizard-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 4px;
}
.wizard-metrics {
  display: flex;
  gap: 18px;
  margin-right: auto;
}
.wizard-metrics div {
  display: flex;
  flex-direction: column;
  min-width: 72px;
}
.wizard-metrics strong {
  color: #1a2b26;
  font-size: 20px;
}
.wizard-metrics span {
  color: #65736e;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}
.btn-primary,
.btn-secondary {
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.btn-primary {
  border: none;
  background: #27705d;
  color: #fff;
}
.btn-primary:disabled {
  opacity: 0.5;
  cursor: default;
}
.btn-secondary {
  border: 1px solid #27705d;
  background: #fff;
  color: #27705d;
}
.btn-secondary:disabled {
  opacity: 0.55;
  cursor: default;
}
.link-action {
  border: none;
  background: none;
  color: #27705d;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
.link-action:disabled {
  opacity: 0.55;
  cursor: default;
}
.link-action.danger,
.error {
  color: #b33a3a;
}
.action-message {
  margin: 12px 0 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: #dcefe7;
  color: #1f5c4b;
  font-size: 13px;
}
.saved-lots {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}
.saved-lots span {
  padding: 4px 8px;
  border-radius: 999px;
  background: #f6f8f7;
  color: #1a2b26;
  font-family: monospace;
  font-size: 12px;
}
.summary-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin: 16px 0;
}
.summary-card {
  padding: 14px;
  border: 1px solid #e2e6e3;
  border-radius: 8px;
  background: #fff;
}
.summary-value {
  display: block;
  color: #1f5c4b;
  font-size: 24px;
  font-weight: 800;
}
.summary-label {
  display: block;
  color: #65736e;
  font-size: 12px;
}
.state-message {
  padding: 36px 0;
  color: #5f6b6a;
  text-align: center;
}
.state-message.compact {
  padding: 14px;
}
.offcanvas-backdrop {
  position: fixed;
  inset: 0;
  z-index: 900;
  display: flex;
  justify-content: flex-end;
  background: rgb(15 23 20 / 48%);
}
.offcanvas-panel {
  width: min(1040px, 96vw);
  height: 100vh;
  height: 100dvh;
  overflow-y: auto;
  padding: 16px;
  background: #f6f8f7;
  box-shadow: -18px 0 42px rgb(15 23 20 / 22%);
}
.offcanvas-header {
  position: sticky;
  top: -16px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: -16px -16px 14px;
  padding: 14px 16px;
  border-bottom: 1px solid #e2e6e3;
  background: #fff;
}
.offcanvas-header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}
.offcanvas-header h3 {
  margin: 0;
  font-size: 18px;
}
.close-button {
  display: grid;
  width: 40px;
  height: 40px;
  border: 1px solid #d4d8d5;
  border-radius: 8px;
  background: #fff;
  color: #65736e;
  font-size: 26px;
  line-height: 1;
  cursor: pointer;
  place-items: center;
}
.close-button:hover {
  border-color: #27705d;
  color: #27705d;
}
.offcanvas-panel .panel {
  padding: 14px;
  margin-bottom: 12px;
}
.offcanvas-panel .field-button,
.offcanvas-panel .inline-action-input {
  min-width: 0;
}
.offcanvas-panel .size-suggestions {
  max-width: 100%;
}
.offcanvas-panel .data-table th,
.offcanvas-panel .data-table td {
  padding: 8px;
}
.cell-code {
  font-family: monospace;
  font-weight: 700;
}
.cell-qty {
  font-weight: 700;
}
.pagination {
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: flex-end;
  padding-top: 12px;
  color: #65736e;
  font-size: 13px;
  font-weight: 700;
}
@media (max-width: 920px) {
  .page-heading {
    margin-bottom: 14px;
  }
  h2 {
    font-size: 20px;
  }
  .summary-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }
  .summary-card {
    padding: 10px;
  }
  .summary-value {
    font-size: 21px;
  }
  .offcanvas-panel {
    width: min(840px, 98vw);
    padding: 12px;
  }
  .offcanvas-header {
    top: -12px;
    margin: -12px -12px 12px;
    padding: 12px;
  }
  .history-filters {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .offcanvas-panel .panel {
    padding: 12px;
  }
  .wizard-footer {
    flex-wrap: wrap;
  }
}
@media (max-width: 560px) {
  .page-heading {
    gap: 10px;
    margin-bottom: 12px;
  }
  .page-heading p {
    font-size: 13px;
  }
  .summary-row {
    grid-template-columns: 1fr;
  }
  .page-heading {
    flex-direction: column;
  }
  .page-toolbar {
    width: 100%;
  }
  .page-toolbar .btn-primary {
    width: 100%;
  }
  .history-actions {
    width: 100%;
    align-items: stretch;
    flex-direction: column;
  }
  .history-actions .btn-secondary {
    width: 100%;
  }
  .history-filters {
    grid-template-columns: 1fr;
    padding: 10px;
  }
  .pagination {
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: center;
    gap: 8px;
  }
  .pagination span {
    grid-column: 1 / -1;
  }
  .pagination .btn-secondary {
    width: 100%;
  }
  .offcanvas-panel {
    width: 100vw;
    padding: 12px;
  }
  .offcanvas-backdrop {
    background: #f6f8f7;
  }
  .offcanvas-header {
    top: -12px;
    margin: -12px -12px 12px;
    padding: 12px;
  }
  .offcanvas-header h3 {
    font-size: 16px;
  }
  .panel-header {
    align-items: stretch;
    flex-direction: column;
  }
  .offcanvas-panel .panel-header {
    gap: 8px;
  }
  .offcanvas-panel .field-button,
  .offcanvas-panel .inline-action-input {
    width: 100%;
    min-width: 0;
  }
  .offcanvas-panel .field-button {
    min-height: 48px;
  }
  .offcanvas-panel .size-suggestions {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    max-width: none;
    overflow: visible;
  }
  .offcanvas-panel .size-suggestions button {
    width: 100%;
  }
  .size-suggestions {
    max-width: 100%;
  }
  .row-card-actions {
    gap: 18px;
  }
  .wizard-footer {
    position: sticky;
    bottom: -12px;
    z-index: 3;
    flex-wrap: wrap;
    padding: 10px 0 0;
    background: #fff;
  }
  .wizard-metrics {
    width: 100%;
    margin-right: 0;
    justify-content: space-between;
  }
  .wizard-footer .btn-primary,
  .wizard-footer .btn-secondary {
    flex: 1 1 140px;
  }
  .field-row-2 {
    gap: 8px;
  }
}
</style>
